/**
 * remote_motion — ONE smoothing model for the sector's remote movers (the physics follow-up's item 5, 2026-10-05;
 * audit-sim §E): other pilots in your system and the den hulls another pilot streams.
 *
 * Before: a remote pilot chased the newest `player_state` row with `position.lerp(target, dt·6)` and a streamed den
 * hull its seat with `lerp(seat, dt·4)`. Rows arrive at ~5 Hz (`spaceMove`) / ~3 Hz (`denHullSync`); at hundreds to
 * thousands of m/s each new row is a jump of tens to hundreds of metres, so the chase surged after every row and
 * coasted before the next (a step every 200 ms) and lagged ~⅓ s of travel.
 *
 * PICKED BY MEASUREMENT (verify:remote-smooth replays 5 Hz streams with jitter and a dropped packet at 300 / 1,200 /
 * 2,400 m/s, straight, turning, stopping and teleporting, through the old chase, this model, and the other candidate —
 * dead reckoning with Fiedler's decaying error offset): BUFFERED INTERPOLATION.
 *  - It renders at `now − delay`, between the two bracketing samples, with a MONOTONE cubic Hermite per axis
 *    (Fritsch–Carlson tangent limiting) — velocity is continuous, the per-frame step is the true step, and no segment
 *    ever overshoots its end sample: a ship that stops is drawn stopping at the stop, never past it and back.
 *  - The delay is the observed sample interval plus the 90th percentile of the observed lateness (a few ms on a quiet
 *    link), eased so the playhead never jumps. That keeps the drawn lag under the old chase's (which trailed the newest
 *    row by its own ⅙ s time constant on top of the row's age) — dead reckoning draws closer still but overshoots every
 *    stop by up to a sample interval of travel (566 m at 2.4 km/s), which the brief forbids.
 *  - Sample times come from the SERVER stamps (`updatedAt`) mapped onto the local clock by the minimum observed delay
 *    (the classic minimum-latency clock sync): arrival jitter never enters the curve. A stamp that runs backwards (THE
 *    CLOCK-STEP LAW: this box's wall clock steps back ~2.5 s every ~32 s) is re-stamped one interval on.
 *  - Past the newest sample (a dropped packet) it extrapolates along the end tangent for at most one interval
 *    (`extrapMaxS`), and when samples resume it hands back through a decaying offset (no step).
 *  - A jump the motion cannot explain (a debug teleport, a respawn) or a long silence SNAPS — the buffer restarts.
 *  - Orientation follows the same timeline: the yaw is interpolated along the shortest arc between the bracketing
 *    samples (a slerp about one axis); the velocity (for pitch, for an AI's lead) is the curve's own derivative.
 *
 * Pure: no three, no clocks of its own — callers pass milliseconds (performance.now for arrivals).
 */

export interface RemotePose {
  x: number; y: number; z: number;
  yaw: number;
  /** The drawn velocity (m/s). */
  vx: number; vy: number; vz: number;
  /** True while drawing past the newest sample (a late or dropped row). */
  extrapolating: boolean;
}

export const REMOTE_MOTION = {
  /** Samples kept. */
  ringN: 12,
  /** The sample interval before any is observed (s): spaceMove's 5 Hz. */
  defaultIntervalS: 0.2,
  minIntervalS: 0.02,
  maxIntervalS: 2,
  /** The interval EMA's weight for a new observation. */
  intervalGain: 0.2,
  /** The render delay covers this quantile of the observed lateness, plus a margin (s). */
  latePct: 0.9,
  delayMarginS: 0.008,
  /** Latenesses kept for the quantile; the clock offset's minimum is taken over this many samples. */
  lateN: 24,
  /** The delay eases toward its target at this rate (1/s) — the playhead never jumps. */
  delayRate: 1.5,
  /** Past the newest sample, extrapolate at most this long (s) — and never more than one interval. */
  extrapMaxS: 0.25,
  /** A jump this far beyond what the motion explains (m) — and this many times it — is a teleport: snap. */
  snapMinM: 2000,
  snapFactor: 3,
  /** A silence this long (s) restarts the buffer at the next sample. */
  silenceS: 3,
  /** The hand-back offset decays at this rate (1/s); a hand-back larger than `reentryMaxM` snaps instead. */
  reentryRate: 8,
  reentryMaxM: 5000,
} as const;

interface Sample { t: number; x: number; y: number; z: number; yaw: number }

const wrapPi = (a: number): number => {
  if (!Number.isFinite(a)) return 0;
  let d = a % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2; else if (d < -Math.PI) d += Math.PI * 2;
  return d;
};

/** Fritsch–Carlson limiting of a segment's end tangents (one axis): no overshoot inside the segment. */
function limit(m0: number, m1: number, delta: number): [number, number] {
  if (delta === 0) return [0, 0];
  let a = Math.sign(m0) === Math.sign(delta) ? m0 / delta : 0;
  let b = Math.sign(m1) === Math.sign(delta) ? m1 / delta : 0;
  const r = a * a + b * b;
  if (r > 9) { const k = 3 / Math.sqrt(r); a *= k; b *= k; }
  return [a * delta, b * delta];
}

export class RemoteTrack {
  private readonly s: Sample[] = [];
  private interval: number = REMOTE_MOTION.defaultIntervalS;
  private readonly late: number[] = [];
  private readonly diffs: number[] = [];
  private offset = NaN;           // local ms − stamp ms (the minimum observed)
  private bias = 0;               // added to stamps after a backward clock step
  private lastStamp = NaN;        // corrected
  private lastArrival = NaN;
  private delay: number = REMOTE_MOTION.defaultIntervalS; // s
  private target: number = REMOTE_MOTION.defaultIntervalS; // s — recomputed per sample, never per frame
  private lastPoseAt = NaN;
  private wasExtrap = false;
  private ox = 0; private oy = 0; private oz = 0; // the hand-back offset
  private readonly prev: RemotePose = { x: 0, y: 0, z: 0, yaw: 0, vx: 0, vy: 0, vz: 0, extrapolating: false };
  private havePrev = false;
  /** Counters for telemetry / harnesses. */
  readonly stats = { pushed: 0, snaps: 0, restamped: 0, extrapFrames: 0, handbacks: 0 };

  get size(): number { return this.s.length; }
  get delayS(): number { return this.delay; }
  get intervalS(): number { return this.interval; }

  /** Restart at one sample (a teleport, a silence): the next pose IS this sample. */
  private restart(smp: Sample): void {
    this.s.length = 0;
    this.s.push(smp);
    this.ox = this.oy = this.oz = 0;
    this.wasExtrap = false;
    this.havePrev = false;
  }

  /**
   * A row landed. `arrivalMs` on the local clock (performance.now); `stampMs` the server's `updatedAt` in ms (NaN when
   * the row carries none — arrival times are used then).
   */
  push(arrivalMs: number, stampMs: number, x: number, y: number, z: number, yaw: number): void {
    if (![arrivalMs, x, y, z].every(Number.isFinite)) return;
    const R = REMOTE_MOTION;
    this.stats.pushed++;
    // the sample's time on the local clock
    let t: number;
    if (Number.isFinite(stampMs)) {
      let st = stampMs + this.bias;
      if (Number.isFinite(this.lastStamp) && st <= this.lastStamp) {
        // THE CLOCK-STEP LAW: the server's clock stepped back (or a duplicate) — re-stamp one interval on, for good
        const want = this.lastStamp + this.interval * 1000;
        this.bias += want - st; st = want; this.stats.restamped++;
      } else if (Number.isFinite(this.lastStamp) && Number.isFinite(this.lastArrival)
        && st - this.lastStamp > 10 * this.interval * 1000 && arrivalMs - this.lastArrival < 3 * this.interval * 1000) {
        // a forward step of the server clock (the stamps leapt, the arrivals did not)
        const want = this.lastStamp + (arrivalMs - this.lastArrival);
        this.bias += want - st; st = want; this.stats.restamped++;
      }
      this.lastStamp = st;
      const d = arrivalMs - st;
      this.diffs.push(d); if (this.diffs.length > R.lateN) this.diffs.shift();
      let mn = Infinity; for (const v of this.diffs) if (v < mn) mn = v;
      // an offset that jumped (a new link, a restarted server) re-anchors the times already held
      if (Number.isFinite(this.offset) && Math.abs(mn - this.offset) > 1000) for (const p of this.s) p.t += mn - this.offset;
      this.offset = mn;
      t = st + this.offset;
    } else t = arrivalMs;
    const silence = Number.isFinite(this.lastArrival) && arrivalMs - this.lastArrival > R.silenceS * 1000;
    this.lastArrival = arrivalMs;
    const smp: Sample = { t, x, y, z, yaw: Number.isFinite(yaw) ? yaw : 0 };
    const n = this.s.length;
    if (n === 0 || silence) { this.restart(smp); return; }
    const last = this.s[n - 1];
    if (t <= last.t) { // the same instant again: keep the newer position
      last.x = x; last.y = y; last.z = z; last.yaw = smp.yaw;
      return;
    }
    const dt = (t - last.t) / 1000;
    this.interval += (Math.min(R.maxIntervalS, Math.max(R.minIntervalS, dt)) - this.interval) * R.intervalGain;
    // a jump the motion does not explain is a teleport
    let ex = last.x, ey = last.y, ez = last.z, sp = 0;
    if (n >= 2) {
      const p = this.s[n - 2], h = Math.max(1e-3, (last.t - p.t) / 1000);
      const vx = (last.x - p.x) / h, vy = (last.y - p.y) / h, vz = (last.z - p.z) / h;
      ex += vx * dt; ey += vy * dt; ez += vz * dt; sp = Math.hypot(vx, vy, vz);
    }
    const miss = Math.hypot(x - ex, y - ey, z - ez);
    if (miss > Math.max(R.snapMinM, R.snapFactor * (sp * dt + 50))) { this.stats.snaps++; this.restart(smp); return; }
    this.s.push(smp);
    if (this.s.length > R.ringN) this.s.shift();
    this.late.push(Math.max(0, arrivalMs - t)); if (this.late.length > R.lateN) this.late.shift();
    let q = 0;
    if (this.late.length) {
      const a = [...this.late].sort((u, v) => u - v);
      q = a[Math.min(a.length - 1, Math.floor(R.latePct * (a.length - 1) + 0.5))] / 1000;
    }
    this.target = Math.min(3 * this.interval + 0.25, Math.max(0.5 * this.interval, this.interval + q + R.delayMarginS));
  }

  /** The delay the playhead is easing toward (s): the interval + the lateness quantile + a margin. */
  delayTarget(): number { return this.target; }

  /** The drawn pose at `nowMs` (local clock), written into `out`. Null before the first sample. */
  pose(nowMs: number, out: RemotePose): RemotePose | null {
    const R = REMOTE_MOTION;
    const n = this.s.length;
    if (n === 0 || !Number.isFinite(nowMs)) return null;
    const dtPose = Number.isFinite(this.lastPoseAt) ? Math.max(0, (nowMs - this.lastPoseAt) / 1000) : 0;
    this.lastPoseAt = nowMs;
    this.delay += (this.delayTarget() - this.delay) * Math.min(1, dtPose * R.delayRate);
    const rt = nowMs - this.delay * 1000;
    out.extrapolating = false;
    if (n === 1 || rt <= this.s[0].t) {
      const a = this.s[0];
      out.x = a.x; out.y = a.y; out.z = a.z; out.yaw = a.yaw; out.vx = out.vy = out.vz = 0;
    } else if (rt >= this.s[n - 1].t) {
      // past the newest: bounded extrapolation along the end tangent
      const a = this.s[n - 2], b = this.s[n - 1];
      const h = Math.max(1e-3, (b.t - a.t) / 1000);
      const vx = (b.x - a.x) / h, vy = (b.y - a.y) / h, vz = (b.z - a.z) / h;
      const tau = Math.min((rt - b.t) / 1000, R.extrapMaxS, this.interval);
      out.x = b.x + vx * tau; out.y = b.y + vy * tau; out.z = b.z + vz * tau;
      out.yaw = b.yaw;
      const moving = (rt - b.t) / 1000 < Math.min(R.extrapMaxS, this.interval);
      out.vx = moving ? vx : 0; out.vy = moving ? vy : 0; out.vz = moving ? vz : 0;
      out.extrapolating = true;
      this.stats.extrapFrames++;
    } else {
      let i = n - 2;
      while (i > 0 && this.s[i].t > rt) i--;
      const a = this.s[i], b = this.s[i + 1];
      const h = (b.t - a.t) / 1000, u = (rt - a.t) / 1000 / h;
      const pa = i > 0 ? this.s[i - 1] : null, pb = i + 2 < n ? this.s[i + 2] : null;
      const tan = (k: 'x' | 'y' | 'z'): [number, number] => {
        const d = (b[k] - a[k]) / h;
        const m0 = pa ? (b[k] - pa[k]) / ((b.t - pa.t) / 1000) : d;
        const m1 = pb ? (pb[k] - a[k]) / ((pb.t - a.t) / 1000) : d;
        return limit(m0, m1, d);
      };
      const u2 = u * u, u3 = u2 * u;
      const h00 = 2 * u3 - 3 * u2 + 1, h10 = u3 - 2 * u2 + u, h01 = -2 * u3 + 3 * u2, h11 = u3 - u2;
      const d00 = 6 * u2 - 6 * u, d10 = 3 * u2 - 4 * u + 1, d01 = -6 * u2 + 6 * u, d11 = 3 * u2 - 2 * u;
      const [mx0, mx1] = tan('x'), [my0, my1] = tan('y'), [mz0, mz1] = tan('z');
      out.x = h00 * a.x + h10 * h * mx0 + h01 * b.x + h11 * h * mx1;
      out.y = h00 * a.y + h10 * h * my0 + h01 * b.y + h11 * h * my1;
      out.z = h00 * a.z + h10 * h * mz0 + h01 * b.z + h11 * h * mz1;
      out.vx = (d00 * a.x + d10 * h * mx0 + d01 * b.x + d11 * h * mx1) / h;
      out.vy = (d00 * a.y + d10 * h * my0 + d01 * b.y + d11 * h * my1) / h;
      out.vz = (d00 * a.z + d10 * h * mz0 + d01 * b.z + d11 * h * mz1) / h;
      out.yaw = a.yaw + wrapPi(b.yaw - a.yaw) * u;
    }
    out.yaw = wrapPi(out.yaw);
    // the hand-back: leaving an extrapolation re-joins the curve through a decaying offset (no step)
    if (this.havePrev && this.wasExtrap && !out.extrapolating) {
      const ex = this.prev.x + this.prev.vx * dtPose, ey = this.prev.y + this.prev.vy * dtPose, ez = this.prev.z + this.prev.vz * dtPose;
      const gx = ex - out.x, gy = ey - out.y, gz = ez - out.z;
      if (Math.hypot(gx, gy, gz) < R.reentryMaxM) { this.ox = gx; this.oy = gy; this.oz = gz; this.stats.handbacks++; }
    }
    this.wasExtrap = out.extrapolating;
    if (this.ox !== 0 || this.oy !== 0 || this.oz !== 0) {
      // the offset is relaxed BEFORE it is drawn, so the hand-back frame itself moves (never a held frame)
      const k = Math.exp(-R.reentryRate * dtPose);
      this.ox *= k; this.oy *= k; this.oz *= k;
      if (Math.hypot(this.ox, this.oy, this.oz) < 1e-3) this.ox = this.oy = this.oz = 0;
      out.x += this.ox; out.y += this.oy; out.z += this.oz;
    }
    this.prev.x = out.x; this.prev.y = out.y; this.prev.z = out.z;
    this.prev.vx = out.vx; this.prev.vy = out.vy; this.prev.vz = out.vz;
    this.havePrev = true;
    return out;
  }
}

/** A blank pose to write into (callers keep one per track / per frame). */
export const makeRemotePose = (): RemotePose => ({ x: 0, y: 0, z: 0, yaw: 0, vx: 0, vy: 0, vz: 0, extrapolating: false });
